import { useTranslation } from 'react-i18next';
import { useState } from 'react';

export default function Projects() {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState('all');

  const projects = [
    // Enterprise & AI Solutions
    {
      title: t('enterprise4Title'),
      category: t('enterprise4Category'),
      description: t('enterprise4Description'),
      image: '/images/Enterprise-4.webp',
      filterType: 'enterprise',
      link: 'https://ai.5vix.com/'
    },
    {
      title: t('enterprise3Title'),
      category: t('enterprise3Category'),
      description: t('enterprise3Description'),
      image: '/images/Enterprise-3.webp',
      filterType: 'enterprise',
      link: 'https://wyzon.debugin.com/'
    },
    {
      title: t('enterprise2Title'),
      category: t('enterprise2Category'),
      description: t('enterprise2Description'),
      image: '/images/Enterprise-2.webp',
      filterType: 'enterprise',
      link: 'https://texio.debugin.com/'
    },
    {
      title: t('enterprise1Title'),
      category: t('enterprise1Category'),
      description: t('enterprise1Description'),
      image: '/images/Enterprise-1.webp',
      filterType: 'enterprise',
      link: 'https://5vix.com/'
    },

    // Mobile Apps
    {
      title: t('mobile2Title'),
      category: t('mobile2Category'),
      description: t('mobile2Description'),
      image: '/images/MOBILE-2.webp',
      filterType: 'mobile',
      androidLink: 'https://play.google.com/store/apps/details?id=com.texio',
      iosLink: 'https://apps.apple.com/eg/app/texio/id6771163743' 
    },
    {
      title: t('mobile1Title'),
      category: t('mobile1Category'),
      description: t('mobile1Description'),
      image: '/images/MOBILE-1.webp',
      filterType: 'mobile',
      androidLink: 'https://play.google.com/store/apps/details?id=com.fivevix',
      iosLink: 'https://apps.apple.com/eg/app/5vix/id6553996855' 
    },
    {
      title: t('mobile4Title'),
      category: t('mobile4Category'),
      description: t('mobile4Description'),
      image: '/images/MOBILE-4.webp',
      filterType: 'mobile',
      androidLink: 'https://play.google.com/store/apps/details?id=com.fivevix.fit',
      iosLink: 'https://apps.apple.com/eg/app/5vix-fit/id6751123751'
    },
    {
      title: t('mobile3Title'),
      category: t('mobile3Category'),
      description: t('mobile3Description'),
      image: '/images/MOBILE-3.webp',
      filterType: 'mobile',
      androidLink: 'https://play.google.com/store/apps/details?id=com.horecain',
      iosLink: 'https://apps.apple.com/us/app/horeca-in/id6758938927' 
    },

    // Web Platforms
    {
      title: t('web2Title'),
      category: t('web2Category'),
      description: t('web2Description'),
      image: '/images/WEB-2.webp',
      filterType: 'web'
    },
    {
      title: t('web3Title'),
      category: t('web3Category'),
      description: t('web3Description'),
      image: '/images/WEB-3.webp',
      filterType: 'web',
      link: 'https://horecasmart-academy.com/'
    },
    {
      title: t('web1Title'),
      category: t('web1Category'),
      description: t('web1Description'),
      image: '/images/WEB-1.webp',
      filterType: 'web',
      link: 'https://northafricafortrading-eg.com/'
    }
  ];

  const filters = [
    { id: 'all', label: t('filterAll') },
    { id: 'web', label: t('filterWeb') },
    { id: 'mobile', label: t('filterMobile') },
    { id: 'enterprise', label: t('filterEnterprise') }
  ];

  const filteredProjects = activeFilter === 'all'
    ? projects
    : projects.filter(project => project.filterType === activeFilter);

  const projectsSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    'itemListElement': projects.map((project, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'item': {
        '@type': 'CreativeWork',
        'name': project.title,
        'description': project.description,
        'genre': project.category,
        'image': project.image
      }
    }))
  };

  const handleProjectClick = (project: any) => {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    
    if (project.filterType === 'mobile') {
      const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
      const isAndroid = /android/i.test(userAgent);

      if (isIOS && project.iosLink) {
        window.open(project.iosLink, '_blank', 'noopener,noreferrer');
      } else if (isAndroid && project.androidLink) {
        window.open(project.androidLink, '_blank', 'noopener,noreferrer');
      } else {
        // Fallback for desktop or if specific link is missing
        const fallbackLink = project.link || project.androidLink || project.iosLink;
        if (fallbackLink) {
          window.open(fallbackLink, '_blank', 'noopener,noreferrer');
        }
      }
    } else if (project.link) {
      window.open(project.link, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <section id="projects" className="py-24 bg-white" itemScope itemType="https://schema.org/ItemList">
      <script type="application/ld+json">
        {JSON.stringify(projectsSchema)}
      </script>
      <meta itemProp="name" content="Debug Portfolio Projects" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-[rgb(0,0,139)]/10 rounded-full px-4 py-2 mb-4">
            <span className="text-[rgb(0,0,139)] text-sm font-semibold">{t('ourWork')}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('projectsTitle')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('projectsDescription')}
          </p>
        </div>

        {/* Filter Section */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-white rounded-full p-1 shadow-md">
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`px-6 py-2 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeFilter === filter.id
                    ? 'bg-[rgb(0,0,139)] text-white'
                    : 'text-gray-600 hover:text-[rgb(0,0,139)]'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 cursor-pointer group"
              itemScope
              itemProp="itemListElement"
              itemType="https://schema.org/ListItem"
              onClick={() => handleProjectClick(project)}
            >
              <meta itemProp="position" content={String(index + 1)} />
              <div className="relative h-48 w-full overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  title={project.title}
                  loading="lazy"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute top-4 left-4 rtl:left-auto rtl:right-4">
                  <span className="bg-[rgb(0,0,139)] text-white px-3 py-1 rounded-full text-xs font-semibold">
                    {project.category}
                  </span>
                </div>
              </div>

              <div className="p-6" itemScope itemType="https://schema.org/CreativeWork" itemProp="item">
                <h3 itemProp="name" className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                <p itemProp="description" className="text-gray-600 text-sm leading-relaxed">{project.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}